import { Type, Dom, Event, Tag, Runtime } from 'main.core';
import type { Menu, MenuOptions } from 'main.popup';

import { SplitButton, type ButtonCounterOptions } from 'ui.buttons';
import { Icon } from 'ui.icon-set.api.core';
import type { Tooltip } from 'ui.system.tooltip';
import 'ui.cnt';

import BaseButton, { bindButtonEvent, unbindButtonEvent } from '../base-button';
import LegacyMenuController from '../menu/legacy-menu-controller';
import SystemMenuController from '../menu/system-menu-controller';
import type MenuController from '../menu/menu-controller';
import { hasMenuItems, type MenuControllerCallbacks } from '../menu/menu-controller';
import ButtonColor from './button-color';
import ButtonSize from './button-size';
import ButtonIcon from './button-icon';
import ButtonState from './button-state';
import ButtonStyle from './button-style';
import ButtonTag from './button-tag';
import AirButtonStyle from './air-button-style';
import type { ButtonOptions, SystemMenuOptions } from './button-options';

type TooltipState = {
	container: HTMLElement,
	target: HTMLElement,
	tooltip: ?Tooltip,
	loading: boolean,
	hovered: boolean,
	focused: boolean,
};

type TooltipExtension = {
	Tooltip: Class<Tooltip>,
	TooltipTrigger: { Hover: string, Focus: string },
};

const tooltips: WeakMap<HTMLElement, TooltipState> = new WeakMap();
const EXPLICIT_TOOLTIP = '[title], [data-hint], [data-ui-tooltip]';
let tooltipLoading: ?Promise<TooltipExtension> = null;

const loadTooltipExtension = async (): Promise<TooltipExtension> => {
	tooltipLoading ??= Runtime.loadExtension('ui.system.tooltip');
	const loading = tooltipLoading;

	try
	{
		return await loading;
	}
	finally
	{
		if (tooltipLoading === loading)
		{
			tooltipLoading = null;
		}
	}
};

const getTooltipText = (target: HTMLElement): string => {
	if (target.tagName === 'INPUT')
	{
		return target.value;
	}

	const inner = target.querySelector('.ui-btn-text-inner');
	if (inner)
	{
		return inner.textContent ?? '';
	}

	const text = target.querySelector('.ui-btn-text');

	return Array.from(text?.childNodes ?? [])
		.filter((node) => node.nodeType === Node.TEXT_NODE)
		.map((node) => node.textContent)
		.join('')
	;
};

const destroyTooltip = (state: TooltipState): void => {
	const tooltip = state.tooltip;
	state.tooltip = null;
	state.hovered = false;
	state.focused = false;
	tooltip?.destroy();
};

const canShowTooltip = (state: TooltipState): boolean => {
	const { container, target } = state;

	return target.isConnected
		&& container.contains(target)
		&& Dom.hasClass(container, ButtonStyle.COLLAPSED)
		&& getTooltipText(target).trim().length > 0
		&& !target.closest(EXPLICIT_TOOLTIP)
		&& !target.querySelector(EXPLICIT_TOOLTIP)
	;
};

export const updateCollapsedTooltip = (button: ?Button): void => {
	const state = tooltips.get(button?.button);
	if (!state)
	{
		return;
	}

	if (canShowTooltip(state))
	{
		state.tooltip?.setOptions({ content: getTooltipText(state.target) });
	}
	else
	{
		destroyTooltip(state);
	}
};

const initializeCollapsedTooltip = (button: Button): void => {
	const container = button.button;
	if (tooltips.has(container))
	{
		updateCollapsedTooltip(button);

		return;
	}

	const target = container.querySelector('.ui-btn-main') ?? container;
	const state: TooltipState = {
		container,
		target,
		tooltip: null,
		loading: false,
		hovered: false,
		focused: false,
	};
	tooltips.set(container, state);

	const handleGesture = async (event: PointerEvent | FocusEvent): Promise<void> => {
		updateCollapsedTooltip(button);
		if (event.type === 'pointerover' || event.target !== target)
		{
			return;
		}

		if (event.type === 'pointerenter')
		{
			state.hovered = event.pointerType !== 'touch';
			if (!state.hovered)
			{
				return;
			}
		}
		else
		{
			state.focused = target.matches(':focus-visible');
			if (!state.focused)
			{
				return;
			}
		}

		if (state.tooltip || state.loading || !canShowTooltip(state))
		{
			return;
		}

		state.loading = true;
		try
		{
			const { Tooltip, TooltipTrigger } = await loadTooltipExtension();
			const hasGesture = (state.hovered && target.matches(':hover'))
				|| (state.focused && target.matches(':focus-visible'))
			;
			if (!canShowTooltip(state) || !hasGesture)
			{
				return;
			}

			state.tooltip = new Tooltip({
				target,
				content: getTooltipText(target),
				trigger: [TooltipTrigger.Hover, TooltipTrigger.Focus],
				events: { onHide: () => destroyTooltip(state) },
			});
		}
		catch (error)
		{
			console.warn('BX.UI.Button: cannot load collapsed button tooltip.', error);
		}
		finally
		{
			state.loading = false;
		}
	};

	Event.bind(target, 'pointerenter', handleGesture, { capture: true });
	Event.bind(target, 'pointerover', handleGesture, { capture: true });
	Event.bind(target, 'focusin', handleGesture, { capture: true });
	Event.bind(target, 'pointerleave', () => {
		state.hovered = false;
	});
	Event.bind(target, 'focusout', () => {
		state.focused = false;
	});
	Event.bind(target, 'keydown', (event: KeyboardEvent) => {
		if (event.key === 'Escape')
		{
			state.hovered = false;
			state.focused = false;
		}
	});
};

/**
 * @namespace {BX.UI}
 */
export default class Button extends BaseButton
{
	static BASE_CLASS = 'ui-btn';

	#style: ?string;
	#isWide: boolean = false;
	#menuController: ?MenuController = null;
	#layout: {
		icon: HTMLElement,
	} = {};

	constructor(options: ButtonOptions)
	{
		super({
			dependOnTheme: options.className?.includes(ButtonStyle.DEPEND_ON_THEME),
			...(Type.isPlainObject(options) ? options : {}),
			baseClass: Type.isStringFilled(options?.baseClass) ? options.baseClass : Button.BASE_CLASS,
		});

		this.isDependOnTheme = null;
		this.size = null;
		this.color = null;
		this.icon = null;
		this.state = null;
		this.id = null;
		this.context = null;

		this.menuWindow = null;
		this.handleMenuClick = this.handleMenuClick.bind(this);

		this.setDependOnTheme(this.options.dependOnTheme ?? false);
		this.setSize(this.options.size);
		this.setColor(this.options.color);
		this.setIcon(this.options.icon, this.options.iconPosition || 'left');
		this.setState(this.options.state);
		this.setId(this.options.id);
		this.#setInitialMenu();
		this.setContext(this.options.context);
		this.setWide(this.options.wide === true);
		this.setLeftCorners(this.options.removeLeftCorners !== true);
		this.setRightCorners(this.options.removeRightCorners !== true);
		if (this.options.collapsedIcon)
		{
			this.setCollapsedIcon(this.options.collapsedIcon);
		}

		if (this.hasAirDesign())
		{
			this.setStyle(this.options.style || AirButtonStyle.FILLED);
			this.setNoCaps(true);

			if (!this.text && !(this instanceof SplitButton))
			{
				this.setCollapsed(true);
			}
		}

		if (this.options.noCaps)
		{
			this.setNoCaps();
		}

		if (this.options.round)
		{
			this.setRound();
		}

		if (this.options.dropdown || (this.#menuController !== null && this.options.dropdown !== false))
		{
			this.setDropdown();
		}

		initializeCollapsedTooltip(this);
	}

	static Size = ButtonSize;
	static Color = ButtonColor;
	static State = ButtonState;
	static Icon = ButtonIcon;
	static Tag = ButtonTag;
	static Style = ButtonStyle;
	static AirStyle = AirButtonStyle;

	setText(text: string): this
	{
		super.setText(text);

		if (this.hasAirDesign())
		{
			Dom.toggleClass(this.getContainer(), ButtonStyle.COLLAPSED, !this.text);
		}

		updateCollapsedTooltip(this);

		return this;
	}

	setProps(props: { [propertyName: string]: string }): this
	{
		super.setProps(props);
		updateCollapsedTooltip(this);

		return this;
	}

	setDataSet(props: { [propertyName: string]: string }): this
	{
		super.setDataSet(props);
		updateCollapsedTooltip(this);

		return this;
	}

	setSize(size: ButtonSize | null): this
	{
		return this.setProperty('size', size, ButtonSize);
	}

	getSize(): ButtonSize | null
	{
		return this.size;
	}

	setColor(color: ButtonColor | null): this
	{
		return this.setProperty('color', color, ButtonColor);
	}

	getColor(): ButtonColor | null
	{
		return this.color;
	}

	setIcon(icon: ?string, iconPosition: 'right' | 'left' = 'left'): this
	{
		if (icon && !icon.startsWith('ui-btn-icon') && !icon.startsWith('ui-icon-set'))
		{
			this.#layout.icon?.remove();
			this.#layout.icon = new Icon({ icon }).render();
			Dom.addClass(this.getContainer(), '--with-icon');
			Dom.prepend(this.#layout.icon, this.getContainer());

			return this;
		}

		this.setProperty('icon', icon, ButtonIcon);

		const iconClass = {
			left: '--with-left-icon',
			right: '--with-right-icon',
		}[iconPosition] ?? '';

		Dom.removeClass(this.getContainer(), '--with-icon');
		Dom.toggleClass(this.getContainer(), ['ui-icon-set__scope', iconClass], Boolean(icon));

		if (this.isInputType() && this.getIcon() !== null)
		{
			throw new Error('BX.UI.Button: Input type button cannot have an icon.');
		}

		return this;
	}

	setCollapsedIcon(icon: ButtonIcon | null): this
	{
		this.setProperty('icon', icon, ButtonIcon);

		Dom.toggleClass(this.getContainer(), ['ui-icon-set__scope', '--with-collapsed-icon'], Boolean(icon));
	}

	getIcon(): ButtonIcon | null
	{
		return this.icon;
	}

	setState(state: ButtonState | null): this
	{
		return this.setProperty('state', state, ButtonState);
	}

	getState(): ButtonState | null
	{
		return this.state;
	}

	setNoCaps(noCaps: boolean = true): this
	{
		Dom.toggleClass(this.getContainer(), ButtonStyle.NO_CAPS, noCaps);

		return this;
	}

	isNoCaps(): boolean
	{
		return Dom.hasClass(this.getContainer(), ButtonStyle.NO_CAPS);
	}

	setRound(round: boolean = true): this
	{
		Dom.toggleClass(this.getContainer(), ButtonStyle.ROUND, round);

		return this;
	}

	isRound(): boolean
	{
		return Dom.hasClass(this.getContainer(), ButtonStyle.ROUND);
	}

	setDependOnTheme(dependOnTheme: boolean = true): this
	{
		Dom.toggleClass(this.getContainer(), ButtonStyle.DEPEND_ON_THEME, dependOnTheme);

		return this;
	}

	setDropdown(dropdown: boolean = true): this
	{
		Dom.toggleClass(this.getContainer(), ButtonStyle.DROPDOWN, dropdown);

		return this;
	}

	isDropdown(): boolean
	{
		return Dom.hasClass(this.getContainer(), ButtonStyle.DROPDOWN);
	}

	setCollapsed(collapsed: boolean = true): this
	{
		const isAirWithoutText = this.hasAirDesign() && !this.getText();
		Dom.toggleClass(this.getContainer(), ButtonStyle.COLLAPSED, collapsed || isAirWithoutText);
		updateCollapsedTooltip(this);

		return this;
	}

	isCollapsed(): boolean
	{
		return Dom.hasClass(this.getContainer(), ButtonStyle.COLLAPSED);
	}

	// works only with air buttons
	setLeftCorners(withLeftCorners: boolean = true): this
	{
		Dom.toggleClass(this.getContainer(), '--remove-left-corners', !withLeftCorners);

		return this;
	}

	// works only with air buttons
	setRightCorners(withRightCorners: boolean = true): this
	{
		Dom.toggleClass(this.getContainer(), '--remove-right-corners', !withRightCorners);

		return this;
	}

	/**
	 * @protected
	 */
	setMenu(options: MenuOptions): this
	{
		if (hasMenuItems(options))
		{
			this.#removeMenu();

			this.#menuController = new LegacyMenuController(options, this.#getMenuCallbacks());
			this.menuWindow = this.#menuController.getMenu();

			bindButtonEvent(this.getMenuClickElement(), 'click', this.handleMenuClick);
		}
		else if (options === false)
		{
			this.#removeMenu(LegacyMenuController);
		}

		return this;
	}

	/**
	 * Sets a menu rendered by the ui.system.menu extension instead of the main.popup one.
	 * Passing false removes it, while the PHP setter takes null for that. The arrow is a caller's
	 * concern here, unlike the PHP setter, which keeps it in sync itself.
	 */
	setSystemMenu(options: SystemMenuOptions | false): this
	{
		if (hasMenuItems(options))
		{
			this.#removeMenu();
			this.#warnOnLegacyMenuItems(options.items);

			this.#menuController = new SystemMenuController(options, this.#getMenuCallbacks());

			bindButtonEvent(this.getMenuClickElement(), 'click', this.handleMenuClick);
		}
		else if (options === false)
		{
			this.#removeMenu(SystemMenuController);
		}

		return this;
	}

	/**
	 * Returns the menu instance, which is created on the first opening: the ui.system.menu class
	 * is available at runtime only. A menu that has never been opened gives null, unlike
	 * getMenuWindow(), which has its instance right after setMenu().
	 */
	getSystemMenu(): Object | null
	{
		return this.#menuController instanceof SystemMenuController ? this.#menuController.getMenu() : null;
	}

	/**
	 * Unlike getSystemMenu(), tells a kind of the set menu apart even before its first opening.
	 */
	hasSystemMenu(): boolean
	{
		return this.#menuController instanceof SystemMenuController;
	}

	#setInitialMenu(): void
	{
		const { menu, systemMenu } = this.options;

		if (hasMenuItems(systemMenu))
		{
			if (Type.isPlainObject(menu))
			{
				console.warn('BX.UI.Button: the "menu" option is ignored, because "systemMenu" is set.');
			}

			this.setSystemMenu(systemMenu);

			return;
		}

		this.setMenu(menu);
		this.setSystemMenu(systemMenu);
	}

	/**
	 * Without a controller class removes a menu of any kind, otherwise only a menu of the given kind.
	 */
	#removeMenu(controllerClass: ?Function = null): void
	{
		const controller = this.#menuController;
		if (controller === null)
		{
			return;
		}

		if (controllerClass !== null && !(controller instanceof controllerClass))
		{
			return;
		}

		unbindButtonEvent(this.getMenuClickElement(), 'click', this.handleMenuClick);

		controller.destroy();

		// a consumer handler of the destroyed menu may set a new one, and it must survive
		if (this.#menuController !== controller)
		{
			return;
		}

		this.#menuController = null;
		this.menuWindow = null;
	}

	#warnOnLegacyMenuItems(items: Array<Object>): void
	{
		const hasLegacyItems = items.some((item) => Type.isPlainObject(item) && !item.title && item.text);
		if (!hasLegacyItems)
		{
			return;
		}

		console.warn(
			'BX.UI.Button: setSystemMenu() expects ui.system.menu options ("title"), got legacy menu format ("text")',
		);
	}

	#getMenuCallbacks(): MenuControllerCallbacks
	{
		return {
			getBindElement: () => this.getMenuBindElement(),
			onShow: () => this.handleMenuShow(),
			onClose: () => this.handleMenuClose(),
		};
	}

	getMenuBindElement(): HTMLElement
	{
		return this.getContainer();
	}

	getMenuClickElement(): HTMLElement
	{
		return this.getContainer();
	}

	/**
	 * @protected
	 */
	handleMenuClick(event: MouseEvent): void
	{
		const opening = this.#menuController?.show();
		if (opening instanceof Promise)
		{
			// the button does not wait for an asynchronous menu, but its failure must not be silent
			opening.catch((error) => {
				console.error('BX.UI.Button: cannot show a menu', error);
			});
		}
	}

	/**
	 * @protected
	 */
	handleMenuShow(): void
	{
		this.setActive(true);
	}

	setAirDesign(use: boolean) {
		super.setAirDesign(use);

		const isButtonInOldFormat = !this.getContainer()?.querySelector('.ui-btn-text-inner');

		if (this.hasAirDesign() && isButtonInOldFormat)
		{
			Dom.remove(this.textNode);
			this.textNode = null;
			this.setText(this.getText());
		}
	}

	/**
	 * @protected
	 */
	handleMenuClose(): void
	{
		// a system menu resets only the state it has set itself, a legacy one resets it as it always did
		if (!this.hasSystemMenu() || this.isActive())
		{
			this.setActive(false);
		}
	}

	getMenuWindow(): Menu | null
	{
		return this.menuWindow;
	}

	setId(id: string | null): this
	{
		if (Type.isStringFilled(id) || Type.isNull(id))
		{
			this.id = id;
		}

		return this;
	}

	getId(): ?string
	{
		return this.id;
	}

	setActive(active: boolean = true): this
	{
		return this.setState(active ? ButtonState.ACTIVE : null);
	}

	isActive(): boolean
	{
		return this.getState() === ButtonState.ACTIVE;
	}

	setHovered(hovered: boolean = true): this
	{
		return this.setState(hovered ? ButtonState.HOVER : null);
	}

	isHover(): boolean
	{
		return this.getState() === ButtonState.HOVER;
	}

	setDisabled(disabled: boolean = true): this
	{
		this.setState(disabled ? ButtonState.DISABLED : null);
		super.setDisabled(disabled);

		return this;
	}

	isDisabled(): boolean
	{
		return this.getState() === ButtonState.DISABLED;
	}

	setWaiting(waiting: boolean = true): this
	{
		this.setState(waiting ? ButtonState.WAITING : null);
		this.setProps({ disabled: waiting ? true : null });

		return this;
	}

	isWaiting(): boolean
	{
		return this.getState() === ButtonState.WAITING;
	}

	setClocking(clocking: boolean = true): this
	{
		this.setState(clocking ? ButtonState.CLOCKING : null);
		this.setProps({ disabled: clocking ? true : null });

		return this;
	}

	isClocking(): boolean
	{
		return this.getState() === ButtonState.CLOCKING;
	}

	/**
	 * @protected
	 */
	setProperty(property: string, value?: any, enumeration: Object): this
	{
		if (this.isEnumValue(value, enumeration))
		{
			Dom.removeClass(this.getContainer(), this[property]);
			Dom.addClass(this.getContainer(), value);
			this[property] = value;
		}
		else if (value === null)
		{
			Dom.removeClass(this.getContainer(), this[property]);
			this[property] = null;
		}

		return this;
	}

	setContext(context: any): this
	{
		if (!Type.isUndefined(context))
		{
			this.context = context;
		}

		return this;
	}

	getContext(): any
	{
		return this.context;
	}

	setWide(isWide: boolean): this
	{
		this.#isWide = isWide === true;

		Dom.toggleClass(this.getContainer(), '--wide', this.#isWide);

		return this;
	}

	isWide(): boolean
	{
		return this.#isWide;
	}

	// This method works only with useAirDesign: true option
	setStyle(style: string): void
	{
		if (this.hasAirDesign() === false)
		{
			console.warn('Style option works only with air buttons.');

			return;
		}

		if (Object.values(AirButtonStyle).includes(style) === false)
		{
			console.warn('Undefined style option. Use value from AirButtonStyle');

			return;
		}

		Dom.removeClass(this.getContainer(), this.#style);
		Dom.addClass(this.getContainer(), style);

		this.#style = style;
	}

	getStyle(): string
	{
		return this.#style;
	}

	setLeftCounter(options: ButtonCounterOptions | null): this
	{
		super.setLeftCounter(this.prepareCounterOptions(options));

		return this;
	}

	setRightCounter(options: ButtonCounterOptions | null): this
	{
		super.setRightCounter(this.prepareCounterOptions(options));

		return this;
	}

	/**
	 * @protected
	 */
	prepareCounterOptions(options: ButtonCounterOptions | null): ButtonCounterOptions | null
	{
		if (!options)
		{
			return null;
		}

		return {
			...options,
			...(this.getSize() ? { size: this.getSize() } : {}),
		};
	}

	startShimmer(): void
	{
		const highlighter = Tag.render`<span class="ui-button__shimmer"></span>`;

		Dom.append(highlighter, this.getContainer());
	}

	stopShimmer(): void
	{
		const highlighter = this.getContainer().querySelector('.ui-button__shimmer');

		Dom.remove(highlighter);
	}
}
